import type { SxProps, Theme } from '@mui/material';
import { COPILOT_SEND_ICON_DATA_URI } from './copilotWebChat.constants';

/**
 * MUI sx overrides for Bot Framework Web Chat DOM structure.
 */
export const copilotWebChatTypographySx: SxProps<Theme> = {
  '& .webchat__basic-transcript': {
    flex: 1,
    minHeight: 0,
    background: '#FAFBFC',
    padding: '12px 14px !important',
  },
  '& .webchat__send-box': {
    flexShrink: 0,
    position: 'relative',
    zIndex: 11,
    padding: '12px 16px 16px !important',
    backgroundColor: '#FFFFFF !important',
    borderTop: '1px solid rgba(168, 85, 247, 0.08) !important',
    boxShadow: '0 -6px 24px rgba(15, 23, 42, 0.04) !important',
  },
  '& .webchat__send-box__main': {
    display: 'flex !important',
    alignItems: 'center !important',
    gap: '10px !important',
    minHeight: '44px !important',
    padding: '5px 5px 5px 16px !important',
    borderRadius: '12px !important',
    boxSizing: 'border-box !important',
    border: '1px solid #E2E8F0 !important',
    borderTop: '1px solid #E2E8F0 !important',
    backgroundColor: '#F8FAFC !important',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
    '&:focus-within': {
      borderColor: 'rgba(0, 120, 212, 0.4) !important',
      borderTopColor: 'rgba(0, 120, 212, 0.4) !important',
      backgroundColor: '#FFFFFF !important',
      boxShadow: '0 0 0 3px rgba(0, 120, 212, 0.1) !important',
    },
  },
  '& .webchat__upload-button, & .webchat__send-box__button--upload': {
    display: 'none !important',
  },
  '& .webchat__send-box-text-box': {
    flex: '1 1 auto !important',
    display: 'flex !important',
    alignItems: 'center !important',
    alignSelf: 'stretch !important',
    margin: '0 !important',
    padding: '0 !important',
    minHeight: '0 !important',
    background: 'transparent !important',
    border: 'none !important',
    boxShadow: 'none !important',
  },
  '& .webchat__send-box-text-box__input, & .webchat__send-box-text-box__html-text': {
    width: '100% !important',
    fontSize: '0.8125rem !important',
    lineHeight: '1.25 !important',
    color: '#0F172A !important',
    background: 'transparent !important',
    border: 'none !important',
    outline: 'none !important',
    padding: '0 !important',
    margin: '0 !important',
    minHeight: '20px !important',
    maxHeight: '120px !important',
    resize: 'none !important',
    fontFamily:
      '"Segoe UI", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif !important',
    '&::placeholder': {
      color: '#94A3B8 !important',
      opacity: '1 !important',
    },
  },
  '& .webchat__send-box-text-box__input:disabled': {
    opacity: '1 !important',
    color: '#64748B !important',
    WebkitTextFillColor: '#64748B !important',
    cursor: 'not-allowed !important',
  },
  '& .webchat__send-box__button': {
    width: '40px !important',
    height: '40px !important',
    minWidth: '40px !important',
    margin: '0 !important',
    padding: '0 !important',
    borderRadius: '10px !important',
    border: 'none !important',
    flexShrink: '0 !important',
    display: 'inline-flex !important',
    alignItems: 'center !important',
    justifyContent: 'center !important',
    backgroundColor: '#0078D4 !important',
    backgroundImage: `${COPILOT_SEND_ICON_DATA_URI} !important`,
    backgroundRepeat: 'no-repeat !important',
    backgroundPosition: 'center !important',
    backgroundSize: '18px 18px !important',
    boxShadow: '0 1px 2px rgba(0, 120, 212, 0.28) !important',
    cursor: 'pointer !important',
    transition: 'background-color 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease',
    '&:hover:not(:disabled)': {
      backgroundColor: '#106EBE !important',
      boxShadow: '0 2px 6px rgba(0, 120, 212, 0.32) !important',
    },
    '&:active:not(:disabled)': {
      transform: 'scale(0.96)',
    },
    '& > *': {
      opacity: '0 !important',
      width: '0 !important',
      height: '0 !important',
      overflow: 'hidden !important',
      pointerEvents: 'none !important',
    },
  },
  '& .webchat__send-box-text-box [contenteditable="true"]': {
    display: 'block !important',
    width: '100% !important',
    padding: '0 !important',
    margin: '0 !important',
    lineHeight: '1.25 !important',
  },
  '& .webchat__icon-button.webchat__send-box__button': {
    backgroundImage: `${COPILOT_SEND_ICON_DATA_URI} !important`,
  },
  '& .webchat__send-box__button:disabled': {
    backgroundColor: '#94A3B8 !important',
    backgroundImage: `${COPILOT_SEND_ICON_DATA_URI} !important`,
    boxShadow: 'none !important',
    opacity: '1 !important',
    cursor: 'not-allowed !important',
  },
  '& .webchat__stacked-layout': {
    marginBottom: '10px !important',
  },
  '& .webchat__stacked-layout__avatar': {
    display: 'none',
  },
  '& .webchat__stacked-layout--from-user': {
    justifyContent: 'flex-end !important',
  },
  '& .webchat__bubble': {
    padding: '10px 12px !important',
    borderRadius: '12px !important',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04) !important',
  },
  '& .webchat__bubble:not(.webchat__bubble--from-user)': {
    backgroundColor: '#FFFFFF !important',
    border: '1px solid #E2E8F0 !important',
    borderLeft: '3px solid #0078D4 !important',
    maxWidth: '100%',
    width: '100%',
  },
  '& .webchat__bubble--from-user': {
    backgroundColor: '#F1F5F9 !important',
    border: '1px solid #E2E8F0 !important',
    borderLeft: '1px solid #E2E8F0 !important',
    maxWidth: '88% !important',
  },
  '& .webchat__bubble__content, & .webchat__text-content, & .webchat__markdown': {
    fontSize: '0.8125rem !important',
    lineHeight: '1.55 !important',
    color: '#334155 !important',
    fontFamily:
      '"Segoe UI", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif !important',
  },
  '& .webchat__bubble__content p': {
    margin: '0.35em 0 !important',
  },
  '& .webchat__bubble__content p:first-of-type': {
    marginTop: '0 !important',
  },
  '& .webchat__bubble__content p:last-of-type': {
    marginBottom: '0 !important',
  },
  '& .webchat__bubble__content strong, & .webchat__bubble__content b': {
    fontSize: '0.8125rem !important',
    fontWeight: '600 !important',
    color: '#0F172A !important',
    letterSpacing: '-0.01em',
  },
  '& .webchat__bubble__content h1, & .webchat__bubble__content h2, & .webchat__bubble__content h3': {
    fontSize: '0.875rem !important',
    fontWeight: '600 !important',
    margin: '0.5em 0 0.25em !important',
    lineHeight: '1.35 !important',
    color: '#0F172A !important',
    letterSpacing: '-0.02em',
  },
  '& .webchat__bubble__content ul, & .webchat__bubble__content ol': {
    margin: '0.35em 0 !important',
    paddingLeft: '1.25em !important',
  },
  '& .webchat__bubble__content li': {
    margin: '0.2em 0 !important',
    fontSize: '0.8125rem !important',
    lineHeight: '1.55 !important',
  },
  '& .webchat__bubble__content li::marker': {
    color: '#64748B !important',
  },
  '& .webchat__bubble__content a': {
    fontSize: '0.8125rem !important',
    color: '#0078D4 !important',
    fontWeight: '500 !important',
    textDecoration: 'none !important',
    '&:hover': {
      textDecoration: 'underline !important',
    },
  },
  '& .webchat__bubble__content img': {
    maxWidth: '16px !important',
    maxHeight: '16px !important',
    verticalAlign: 'middle',
    marginRight: '4px',
  },
  '& .webchat__bubble__content hr': {
    margin: '0.65em 0 !important',
    border: 'none !important',
    borderTop: '1px solid #E2E8F0 !important',
  },
  '& .webchat__bubble__content code': {
    fontSize: '0.75rem !important',
    backgroundColor: '#F1F5F9 !important',
    padding: '0.1em 0.35em !important',
    borderRadius: '4px !important',
    color: '#0F172A !important',
  },
  '& .webchat__bubble__content blockquote': {
    margin: '0.5em 0 !important',
    paddingLeft: '0.75em !important',
    borderLeft: '2px solid #CBD5E1 !important',
    color: '#64748B !important',
  },
  '& .webchat__timestamp': {
    display: 'none !important',
  },
  '& .webchat__typing-indicator': {
    display: 'none',
  },
  '& .webchat__stacked-layout:not(.webchat__stacked-layout--from-user) .webchat__stacked-layout__message':
    {
      maxWidth: '100%',
    },
  '& .webchat__stacked-layout:not(.webchat__stacked-layout--from-user) .webchat__stacked-layout__attachment':
    {
      maxWidth: '100%',
    },
};
